import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {ThemeProvider} from '@mui/material/styles';
import {Box, CssBaseline, Typography} from '@mui/material';
import useGalleryTheme from '../theme/useGalleryTheme';
import PublicAppBar from './PublicAppBar';
import {createPublicTheme, galleryColors} from '../theme/publicTheme';
import versionInfo from '../version.json';
import useRestaurareScroll from '../utils/useRestaurareScroll';

const PublicLayout = () => {
    const {pathname} = useLocation();
    // Pasul 38: la „Înapoi” pagina revine unde era (după încărcarea datelor), la navigare nouă începe de sus.
    useRestaurareScroll();
    const login = pathname === '/login';
    const {preference, chooseTheme, mode} = useGalleryTheme();
    const theme = useMemo(() => createPublicTheme(mode), [mode]);
    const colors = galleryColors[mode];
    // Ecranul de încărcare ia tema aleasă (poate diferi de cea a sistemului): fundalul și caseta siglei.
    useLayoutEffect(() => {
        const splash = document.getElementById('splash');
        if (!splash) return;
        splash.style.background = colors.background;
        splash.dataset.theme = mode;
    }, [colors.background, mode]);
    const navbarRef = useRef(null);
    const [navbarHeight, setNavbarHeight] = useState(0);

    useLayoutEffect(() => {
        const navbar = navbarRef.current;
        const updateHeight = () => setNavbarHeight(navbar.getBoundingClientRect().height);
        updateHeight();
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(updateHeight);
            observer.observe(navbar);
            return () => observer.disconnect();
        }
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box data-theme={mode} className={`public-layout${login ? ' public-layout-login' : ''}`}
                sx={{'--gallery-bg': colors.background, '--gallery-paper': colors.paper, '--gallery-text': colors.text, '--gallery-muted': colors.muted, '--gallery-accent': colors.accent, '--gallery-border': colors.border, colorScheme: mode, minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', color: 'text.primary'}}>
                <PublicAppBar navbarRef={navbarRef} preference={preference} onThemeChange={chooseTheme} />
                <Box component="main" className="public-main" sx={{flex: 1, position: 'relative', isolation: 'isolate'}}>
                    <Box aria-hidden="true"
                        sx={{position: 'fixed', top: Math.max(0, navbarHeight - 24), right: 0, width: {xs: '100%', sm: 'auto'},
                            height: `calc(100dvh - ${Math.max(0, navbarHeight - 24)}px)`, zIndex: 0,
                            opacity: mode === 'dark' ? .22 : .18, pointerEvents: 'none', userSelect: 'none',
                            display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start'}}>
                        <Box component="img" src={`${process.env.PUBLIC_URL}/assets/petru-malutan-memorial.webp`} alt=""
                            sx={{display: 'block', width: 'auto', height: '100%', flexShrink: 0, mx: {xs: 'auto', sm: 0},
                                transform: {xs: 'translateX(20vw)', sm: 'none'},
                                maskImage: 'linear-gradient(to right, transparent 0%, #000 42%), linear-gradient(to bottom, #000 55%, transparent 100%)',
                                maskComposite: 'intersect',
                                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 42%), linear-gradient(to bottom, #000 55%, transparent 100%)',
                                WebkitMaskComposite: 'source-in'}} />
                    </Box>
                    <Box sx={{position: 'relative', zIndex: 1, width: '100%', minWidth: 0}}><Outlet /></Box>
                </Box>
                <Box component="footer" sx={{py: 3, px: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider'}}>
                    <Typography variant="body2" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                        In memoria Petru Malutan (1941-2025)...
                        <Box component="span" sx={{ml: 1.5, fontStyle: 'normal', fontSize: '0.7rem', color: 'text.disabled'}}>
                            v{versionInfo.version}
                        </Box>
                    </Typography>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default PublicLayout;
